import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Glass redesign — Phase 2 primitive.
 *
 * Pill-shaped segmented selector. Rendered as a single tablist with
 * exactly one selected option at a time. Used for:
 *   - Topbar view switcher (list / board / calendar / time / …)
 *   - Calendar Day / Week / Month
 *   - Task-detail Activity feed (All / Comments / Activity)
 *
 * Generic over the option's `value` type — pass a discriminated
 * union for type-safe `onChange` handling at the call site.
 *
 * Shape per option:
 *   { value, icon?, label?, kbd? }
 *
 * `icon` and `label` can be combined (icon + text) or used alone
 * (icon-only for the topbar; label-only is rare). `kbd` adds a
 * subtle key-cap inside the segment — used by the Calendar view
 * switcher to teach `D / W / M` shortcuts.
 *
 * Sizes match the prototype: `sm` (24px tall, the topbar default)
 * and `md` (28px, calendar). The `tone` variant lets the active
 * segment use brand-orange instead of the standard primary fill —
 * unused right now but reserved for any "filter is active" cases.
 *
 * a11y: WAI-ARIA tabs pattern. The wrapping element is `role=tablist`
 * and each segment is `role=tab` with `aria-selected`. The consumer
 * is responsible for matching the active option's `value` to its
 * controlled state.
 */

export interface SegmentedControlOption<TValue extends string> {
  value: TValue;
  icon?: ReactNode;
  label?: ReactNode;
  kbd?: string;
  disabled?: boolean;
  ariaLabel?: string;
}

export interface SegmentedControlProps<TValue extends string> {
  options: SegmentedControlOption<TValue>[];
  value: TValue;
  onChange: (next: TValue) => void;
  size?: 'sm' | 'md';
  /** Optional label for the whole tablist, e.g. "View". */
  ariaLabel?: string;
  className?: string;
}

const sizeWrapper: Record<NonNullable<SegmentedControlProps<string>['size']>, string> = {
  sm: 'h-7 p-0.5',
  md: 'h-8 p-1',
};

const sizeButton: Record<NonNullable<SegmentedControlProps<string>['size']>, string> = {
  sm: 'h-6 min-w-[26px] px-2 text-[11.5px]',
  md: 'h-6 min-w-[28px] px-2.5 text-[12px]',
};

export function SegmentedControl<TValue extends string>({
  options,
  value,
  onChange,
  size = 'sm',
  ariaLabel,
  className,
}: SegmentedControlProps<TValue>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-lg border border-border bg-secondary',
        sizeWrapper[size],
        className,
      )}
    >
      {options.map((opt) => {
        const isActive = opt.value === value;
        return (
          <button
            key={opt.value}
            role="tab"
            type="button"
            aria-selected={isActive}
            aria-label={opt.ariaLabel}
            disabled={opt.disabled}
            onClick={() => {
              if (!opt.disabled && opt.value !== value) onChange(opt.value);
            }}
            className={cn(
              'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors duration-100',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-accent)]',
              'disabled:cursor-not-allowed disabled:opacity-40',
              sizeButton[size],
              isActive
                ? 'bg-background text-foreground shadow-sm'
                : 'text-foreground-muted hover:text-foreground',
            )}
          >
            {opt.icon}
            {opt.label}
            {opt.kbd ? (
              <span className="font-mono text-[9.5px] leading-none text-foreground-faint">
                {opt.kbd}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
